import { InfoCircleFilled } from '@ant-design/icons';
import { Col, Flex, Modal, Row, Steps, Typography } from 'antd';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useOrganization } from '../../api/Onboarding';
import { useCreateSurvey } from '../../api/Surveys';
import Loading from '../../components/Loading';
import { QuestionTypeValues } from '../../constants';
import { BluePrimary } from '../../styles';
import CategorizeQuestions from './CategorizeQuestions';
import CodingGuidance from './CodingGuidance';
import FileUploadForm from './FileUploadForm';
import LanguageSettings from './LanguageSettings';
import UploadFailed from './UploadFailed';
import UploadSuccessful from './UploadSuccessful';

const { error } = Modal;
const { Paragraph, Text } = Typography;

const StyledRow = styled(Row)`
  width: 100%;
  padding: 32px 0;
`;

const StyledInfoCircleFilled = styled(InfoCircleFilled)`
  color: ${BluePrimary} !important;
`;

function UploadSurvey() {
  const navigate = useNavigate();

  const [current, setCurrent] = useState(0);
  const [surveyData, setSurveyData] = useState({});
  const [codingGuidanceSelected, setCodingGuidanceSelected] = useState(false);

  const createSurvey = useCreateSurvey();
  const { data: organization, isLoading: organizationLoading } = useOrganization({
    onSuccess: (data) => {
      if (data.data.available_tokens === 0) {
        error({
          title: 'No tokens, no problem!',
          content: (
            <>
              <Paragraph>You&apos;re out of tokens, but we can fix that fast.</Paragraph>
              <Paragraph>
                Contact <a href="mailto:support@fathomthat.ai">support@fathomthat.ai</a> to add
                tokens and get your data processing right away!
              </Paragraph>
            </>
          ),
          icon: null,
          onOk() {
            navigate('/');
          },
        });
      }
    },
  });

  const next = () => {
    setCurrent(current + 1);
  };

  const prev = () => {
    setCurrent(current - 1);
  };

  const retryUpload = () => {
    createSurvey.reset();
    prev();
  };

  const handleUploadSurvey = (totalOEResponses) => {
    const data = new FormData();
    Object.entries(surveyData.surveyDetails).forEach(([key, value]) => {
      if (value) {
        // exclude optional fields that weren't filled
        if (key === 'file') {
          data.append(key, value[0]);
        } else {
          data.append(key, value);
        }
      }
    });

    const questionsMetadata = {};
    const shouldTranslate = surveyData.surveyDetails.translation === true;
    const isEnglish = (code) => code?.toLowerCase()?.startsWith('en');

    surveyData.questions
      .filter((q) => q.columnType !== QuestionTypeValues.REMOVE)
      .forEach((question) => {
        const { index, columnType } = question;
        // Set language field only if user chose to translate and the question is not in English
        const needsTranslation =
          shouldTranslate && question.theme_language && !isEnglish(question.theme_language);
        questionsMetadata[index] = {
          has_themes: columnType === QuestionTypeValues.HAS_THEMES,
          is_filterable:
            columnType === QuestionTypeValues.IS_SINGLE_SELECT ||
            columnType === QuestionTypeValues.IS_MULTI_SELECT,
          is_multi_select: columnType === QuestionTypeValues.IS_MULTI_SELECT,
          updated_question_name: question.updated_question_name,
          is_metadata: columnType === QuestionTypeValues.IS_METADATA,
          is_id_column: columnType === QuestionTypeValues.IS_KEY,
          ...(needsTranslation ? { language: question.theme_language } : {}),
          ...(question.theme_language ? { theme_language: question.theme_language } : {}),
          ...(question.processing_type ? { processing_type: question.processing_type } : {}),
          ...(question.import_code_frame_id
            ? { import_code_frame_id: question.import_code_frame_id }
            : {}),
        };
      });

    data.append('questions_metadata', JSON.stringify(questionsMetadata));
    data.append('total_oe_responses', totalOEResponses);

    createSurvey.mutate({ data });
  };

  const finishUpload = () => {
    const openEndedQuestions = surveyData.questions.filter(
      (q) => q.columnType === QuestionTypeValues.HAS_THEMES,
    );
    const totalOEResponses = openEndedQuestions.reduce(
      (sum, question) => sum + question.responseCount,
      0,
    );
    const availableTokens = organization?.data.available_tokens;
    const tokenMultiplier = organization?.data.token_multiplier || 2;
    const processingType = surveyData?.surveyDetails?.processing_type;
    const isHybrid = organization?.data.account_type === 'Hybrid';

    // Calculate tokens based on account type and processing type
    // Token multiplier only applies to Hybrid organizations when using Magic processing
    // For non-Hybrid (pure Pro or Magic orgs): always 1 token per response
    // For Hybrid with Pro processing: 1 token per response
    // For Hybrid with Magic processing: token_multiplier * responses
    const tokensToDeduct =
      isHybrid && processingType === 'Magic'
        ? totalOEResponses * tokenMultiplier
        : totalOEResponses;

    Modal.confirm({
      title: `This survey will use ${tokensToDeduct} tokens`,
      content: (
        <>
          {isHybrid && (
            <Paragraph>
              <i>
                {processingType === 'Pro'
                  ? `${totalOEResponses} verbatims processed in Pro mode at 1 token each`
                  : `${totalOEResponses} verbatims processed in Magic mode at ${tokenMultiplier} tokens each`}
              </i>
            </Paragraph>
          )}
          <Paragraph>
            You have {availableTokens} tokens now, and will have {availableTokens - tokensToDeduct}{' '}
            after uploading.
          </Paragraph>
          {openEndedQuestions.length > 1 && (
            <Flex vertical>
              <Text strong>Detailed breakdown:</Text>
              {openEndedQuestions.map((q) => {
                const questionTokens =
                  isHybrid && processingType === 'Magic'
                    ? q.responseCount * tokenMultiplier
                    : q.responseCount;
                return (
                  <Text key={q.index}>
                    {q.updated_question_name}: {questionTokens} tokens
                  </Text>
                );
              })}
            </Flex>
          )}
        </>
      ),
      icon: <StyledInfoCircleFilled />,
      okText: "Let's go!",
      onOk() {
        handleUploadSurvey(totalOEResponses);
      },
      width: '50%',
    });
  };

  if (organizationLoading) {
    return <Loading />;
  }

  if (createSurvey.isSuccess) {
    return <UploadSuccessful processingType={surveyData?.surveyDetails?.processing_type} />;
  }

  if (createSurvey.isError) {
    return <UploadFailed retryUpload={retryUpload} />;
  }

  const steps = [
    {
      title: 'Import survey',
      content: (
        <FileUploadForm
          setSurveyData={setSurveyData}
          surveyData={surveyData}
          next={next}
          setCodingGuidanceSelected={setCodingGuidanceSelected}
          organization={organization}
        />
      ),
    },
    {
      title: 'Categorize',
      content: (
        <CategorizeQuestions
          surveyData={surveyData}
          prev={prev}
          next={next}
          setSurveyData={setSurveyData}
          createSurvey={createSurvey}
          availableTokens={organization?.data.available_tokens}
          createSurveyLoading={createSurvey.isLoading}
        />
      ),
    },
    {
      title: 'Language',
      content: (
        <LanguageSettings
          surveyData={surveyData}
          setSurveyData={setSurveyData}
          prev={prev}
          next={next}
          finishUpload={finishUpload}
          createSurveyLoading={createSurvey.isLoading}
          hasNextStep={codingGuidanceSelected}
        />
      ),
    },
  ];

  if (codingGuidanceSelected) {
    steps.push({
      title: 'Select method',
      content: (
        <CodingGuidance
          surveyData={surveyData}
          prev={prev}
          createSurveyLoading={createSurvey.isLoading}
          finishUpload={finishUpload}
          setSurveyData={setSurveyData}
        />
      ),
    });
  }

  return (
    <StyledRow align="center">
      <Col span={18}>
        {steps[current].title !== 'Import survey' && (
          <Steps progressDot current={current} items={steps} />
        )}
      </Col>
      <Col span={18}>{steps[current].content}</Col>
    </StyledRow>
  );
}

export default UploadSurvey;
